import { useEffect } from 'react'
import { useAuth } from './useAuth'
import useWebSocketHook from './useWebSocketHook'
import { useGetNotificationHistory } from '@/hooks/api/notifications'

/**
 * Connects the notification WebSocket for as long as a user is
 * authenticated (any auth shell that mounts this stays live — no more
 * per-layout duplication of the connect/disconnect effect), and hydrates
 * notification history from the REST fallback once per connect so the bell/
 * history page isn't empty for anyone who wasn't online to catch the live
 * push (see useGetNotificationHistory).
 *
 * Needs `AxiosContext` (for the REST hydration call), so it must be rendered
 * from *inside* a layout's own `<AxiosProvider>` — a layout component can't
 * consume the context it itself provides in its own return. Use
 * `NotificationSocketBridge` to mount this from the right place without
 * every shell having to remember that rule.
 */
export const useNotificationSocket = () => {
  const { user } = useAuth()
  const { connectWebSocket, disconnect } = useWebSocketHook()
  const { mutate: fetchHistory } = useGetNotificationHistory()

  useEffect(() => {
    if (!user?.userID) return

    connectWebSocket(user.userName)
    fetchHistory()

    return () => disconnect()
    // Intentionally only keyed on the logged-in user — connectWebSocket/
    // disconnect/fetchHistory aren't referentially stable across renders,
    // and re-running this on their identity would reconnect constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userID])
}

export default useNotificationSocket
